import { useAuth0 } from "@auth0/auth0-react"
import type { APIError } from "@kamae-apps/shared/Types/ApiResponse"
import { useCryptr } from "@killiangabrielkamae/cryptr-react"
import posthog from "posthog-js"
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { useUser } from "../Contexts/User/UserContext"
import { userService } from "../Services/UserServices"
import { fromCodeToLang, sha1 } from "../utils"
import config from "../variable"

interface UseUserLoader {
  isUserLoaded: boolean
  displayNewBelt: boolean
  setDisplayNewBelt: Dispatch<SetStateAction<boolean>>
}

// Drives the shared UserContext from the App root: loads the user once a JWT is
// available, replays the legacy 401/403 logout, persists the language, detects
// belt progression and flips `isUserLoaded` (gating the route guards).
export function useUserLoader(jwt: string | null, featureFlag: string[]): UseUserLoader {
  const { user, loadUser } = useUser()
  const cryptr = useCryptr()
  const auth0 = useAuth0()
  const navigate = useNavigate()
  const location = useLocation()
  const { i18n } = useTranslation("app")

  const [isUserLoaded, setIsUserLoaded] = useState(false)
  const [displayNewBelt, setDisplayNewBelt] = useState(false)

  const persistLanguage = useCallback(
    (lng?: string) => {
      if (!i18n.isInitialized || !lng) {
        return
      }
      userService.updateUserLanguage(lng)
    },
    [i18n.isInitialized]
  )

  // Need to be in a useEffect, if not, it will re-subscribe to languageChanged event on each render
  useEffect(() => {
    i18n.on("languageChanged", persistLanguage)
    return () => {
      i18n.off("languageChanged", persistLanguage)
    }
  }, [persistLanguage, i18n])

  // Load the user once authenticated, replaying the legacy logout flow on 401/403
  useEffect(() => {
    if (!jwt) {
      return
    }
    loadUser().catch(error => {
      const code = (error as APIError).code
      if (code !== 401 && code !== 403) {
        return
      }
      const redirectTo = code === 401 ? "/login?error=unauthorized" : "/forbidden"
      if (cryptr.isAuthenticated()) {
        // We need to use window.location.assign in order to refresh Cryptr SDK values
        cryptr.logOut(() => window.location.assign(config.app + redirectTo), undefined, false)
      } else if (auth0.isAuthenticated) {
        auth0.logout({ returnTo: window.location.origin + redirectTo })
      }
      localStorage.clear()
    })
  }, [jwt, loadUser, cryptr, auth0])

  // Persist the language and detect belt progression once the user is loaded
  useEffect(() => {
    if (typeof user.scope === "undefined") {
      return
    }
    localStorage.setItem("lang", fromCodeToLang(user.languageId))

    const oldBelt = localStorage.getItem("belt")
    if (oldBelt !== null && user.level > Number.parseInt(oldBelt)) {
      setDisplayNewBelt(true)
      sha1(user.email).then(identifier => {
        posthog.identify(identifier)
        posthog.capture("earned new belt", { level: user.level })
      })
    }
    localStorage.setItem("belt", user.level.toString())
  }, [user.scope, user.level, user.languageId, user.email])

  // Redirect away from the bare "/" once the user is known. Kept independent of the
  // feature flags so it still runs in degraded mode when PostHog's /flags/ endpoint is
  // blocked (e.g. Arc/Brave ad blockers).
  useEffect(() => {
    if (
      typeof user.scope === "undefined" ||
      !user.avatar ||
      location.pathname !== "/" ||
      (window.opener !== null && window.name === "LoginPopup")
    ) {
      return
    }
    const redirection = localStorage.getItem("redirectTo")
    if (redirection !== null) {
      localStorage.removeItem("redirectTo")
      navigate(redirection)
    } else {
      navigate("/home")
    }
  }, [user.scope, user.avatar, location.pathname, navigate])

  // Happy path: once the user and the feature flags are loaded, render the app.
  useEffect(() => {
    if (
      typeof user.scope !== "undefined" &&
      featureFlag.length > 0 &&
      (window.opener === null || window.name !== "LoginPopup")
    ) {
      const timeout = setTimeout(() => setIsUserLoaded(true), 500)
      return () => clearTimeout(timeout)
    }
  }, [featureFlag.length, user.scope])

  // Fail-open: PostHog's /flags/ endpoint can be blocked by ad blockers (Arc, Brave),
  // in which case onFeatureFlags never fires and featureFlag stays empty. Rather than
  // blocking the whole app on a loader forever, render it in degraded mode (every flag
  // treated as disabled) once the user is loaded.
  useEffect(() => {
    if (typeof user.scope === "undefined" || (window.opener !== null && window.name === "LoginPopup")) {
      return
    }
    const timeout = setTimeout(() => setIsUserLoaded(true), 3000)
    return () => clearTimeout(timeout)
  }, [user.scope])

  return { isUserLoaded, displayNewBelt, setDisplayNewBelt }
}
